import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Megaphone, X } from "lucide-react";

import { useAuth } from "../context/AuthContext";
import CreatorStudioLayout from "../pages/creator/CreatorStudioLayout";
import {
  BROADCAST_TARGET_OPTIONS,
  fetchBroadcastQuota,
  fetchCreatorBroadcastHistory,
  formatHistoryReach,
  formatReachLabel,
  formatSendButtonLabel,
  sendBroadcast,
} from "../services/broadcastService";
import { formatCount } from "../utils/formatCount";
import "./Broadcasts.css";

const MAX_CHARS = 280;

function formatSentAt(ts) {
  if (!ts?.seconds) return "Just now";
  return new Date(ts.seconds * 1000).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatResetLabel(iso) {
  if (!iso) return "Sunday at 12:00 AM EST";
  try {
    return new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Toronto",
      weekday: "long",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return "Sunday at 12:00 AM EST";
  }
}

function counterTone(len) {
  if (len >= 270) return "red";
  if (len >= 200) return "yellow";
  return "green";
}

export default function Broadcasts() {
  const { currentUser } = useAuth();
  const [message, setMessage] = useState("");
  const [targetTier, setTargetTier] = useState("all");
  const [broadcasts, setBroadcasts] = useState([]);
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (text, type = "success") => {
    setToast({ text, type });
    window.setTimeout(() => setToast(null), 3200);
  };

  const load = useCallback(async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [history, quotaData] = await Promise.all([
        fetchCreatorBroadcastHistory(currentUser.uid),
        fetchBroadcastQuota(),
      ]);
      setBroadcasts(history);
      setQuota(quotaData);
    } catch (err) {
      console.error("Broadcast load error:", err);
      showToast(err.message || "Could not load broadcasts.", "error");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const tierCounts = quota?.tierCounts || {
    all: quota?.subscriberCount || 0,
    supporter: 0,
    inner: 0,
    vip: 0,
  };
  const reachCount = Number(tierCounts[targetTier] ?? 0);

  const trimmed = message.trim();
  const charCount = message.length;
  const canSend =
    Boolean(trimmed)
    && charCount <= MAX_CHARS
    && !sending
    && (quota?.remaining ?? 0) > 0
    && reachCount > 0;

  const remainingLabel = useMemo(() => {
    if (!quota) return "Loading quota…";
    return `You have ${quota.remaining} of ${quota.weeklyLimit || 10} broadcasts remaining this week.`;
  }, [quota]);

  const reachLabel = formatReachLabel(reachCount, targetTier);

  const handleSend = async () => {
    if (!canSend) {
      if (reachCount === 0) {
        showToast(
          targetTier === "all"
            ? "You have no subscribers yet."
            : "No subscribers in that tier.",
          "error"
        );
      } else if ((quota?.remaining ?? 0) <= 0) {
        showToast("You've reached your 10 broadcasts this week.", "error");
      }
      return;
    }

    setSending(true);
    try {
      const result = await sendBroadcast(trimmed, targetTier);
      setMessage("");
      showToast(`Broadcast sent to ${formatReachLabel(result.subscribersReached, result.targetTier || targetTier)}`);
      setQuota((prev) => ({
        ...(prev || {}),
        remaining: result.remaining,
        sentThisWeek: (prev?.weeklyLimit || 10) - result.remaining,
        resetAt: result.resetAt || prev?.resetAt,
      }));
      setBroadcasts((prev) => [
        {
          id: result.broadcastId,
          message: trimmed,
          text: trimmed,
          subscriberCount: result.subscribersReached,
          targetTier: result.targetTier || targetTier,
          status: "sent",
          createdAt: { seconds: Date.now() / 1000 },
          sentAt: { seconds: Date.now() / 1000 },
        },
        ...prev,
      ]);
      // Refresh tier counts after send so UI stays accurate
      fetchBroadcastQuota()
        .then((q) => setQuota((prev) => ({ ...prev, ...q })))
        .catch(() => {});
    } catch (err) {
      showToast(err.message || "Could not send broadcast.", "error");
    } finally {
      setSending(false);
    }
  };

  return (
    <CreatorStudioLayout activePath="/broadcasts">
      <div className="bc-page">
        {toast && (
          <div className={`bc-toast ${toast.type === "error" ? "bc-toast-error" : "bc-toast-success"}`}>
            {toast.type === "error" ? <X size={14} strokeWidth={3} /> : <Check size={14} strokeWidth={3} />}
            {toast.text}
          </div>
        )}

        <header className="bc-header">
          <div className="bc-header-icon" aria-hidden>
            <Megaphone size={22} color="white" strokeWidth={2} />
          </div>
          <div>
            <p className="exact-eyebrow">Broadcasts</p>
            <h1>Tell your fans what&apos;s happening</h1>
            <p className="bc-header-sub">
              You get 10 per week. Make them count — new drop in 3 hours, schedule shifts, get ready.
            </p>
          </div>
        </header>

        <section className="bc-quota" aria-live="polite">
          <strong>{remainingLabel}</strong>
          <span>Next reset: {formatResetLabel(quota?.resetAt)}</span>
          <span className="bc-quota-subs">
            {quota
              ? `${formatCount(quota.subscriberCount || 0)} active subscriber${quota.subscriberCount === 1 ? "" : "s"}`
              : "—"}
          </span>
        </section>

        <section className="bc-compose">
          <div className="bc-target">
            <label className="bc-compose-label" htmlFor="bc-target-tier">
              Send to:
            </label>
            <select
              id="bc-target-tier"
              className="bc-target-select"
              value={targetTier}
              onChange={(e) => setTargetTier(e.target.value)}
              disabled={sending || loading}
            >
              {BROADCAST_TARGET_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                  {opt.value !== "all" && typeof tierCounts[opt.value] === "number"
                    ? ` — ${tierCounts[opt.value]}`
                    : ""}
                </option>
              ))}
            </select>
            <p className="bc-target-reach" aria-live="polite">
              Reaches <strong>{reachLabel}</strong>
            </p>
          </div>

          <label className="bc-compose-label" htmlFor="bc-message">
            What do you want to tell them?
          </label>
          <textarea
            id="bc-message"
            className={`bc-textarea bc-textarea--${counterTone(charCount)}`}
            placeholder="e.g., New drop coming in 3 hours — get ready! 🍁"
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_CHARS))}
            rows={5}
            maxLength={MAX_CHARS}
          />
          <div className={`bc-char-count bc-char-count--${counterTone(charCount)}`}>
            {charCount} / {MAX_CHARS}
          </div>

          {reachCount === 0 && !loading && (
            <p className="bc-hint">
              {targetTier === "all"
                ? "You have no subscribers yet — grow your circle before broadcasting."
                : "No subscribers in that tier yet — pick another audience or grow that circle."}
            </p>
          )}
          {(quota?.remaining ?? 1) <= 0 && (
            <p className="bc-hint bc-hint--warn">Weekly limit reached. Come back after the reset.</p>
          )}

          <div className="bc-actions">
            <button
              type="button"
              className="bc-btn bc-btn-secondary"
              disabled={sending || !message}
              onClick={() => setMessage("")}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bc-btn bc-btn-primary"
              disabled={!canSend}
              onClick={handleSend}
            >
              {sending ? "Sending…" : formatSendButtonLabel(reachCount, targetTier)}
            </button>
          </div>
        </section>

        <section className="bc-history">
          <div className="bc-history-head">
            <h2>Broadcast history</h2>
            <small>{broadcasts.length} sent</small>
          </div>

          {loading ? (
            <div className="bc-empty">Loading broadcasts…</div>
          ) : broadcasts.length === 0 ? (
            <div className="bc-empty">
              <Megaphone size={28} strokeWidth={1.5} />
              <strong>No broadcasts yet</strong>
              <p>Send your first hype note when something&apos;s about to drop.</p>
            </div>
          ) : (
            <div className="bc-history-table" role="table">
              <div className="bc-history-row bc-history-row--head" role="row">
                <span role="columnheader">Message</span>
                <span role="columnheader">Date sent</span>
                <span role="columnheader">Reached</span>
                <span role="columnheader">Status</span>
              </div>
              {broadcasts.map((b) => (
                <div key={b.id} className="bc-history-row" role="row">
                  <span className="bc-history-msg" role="cell">
                    {b.message || b.text || "—"}
                  </span>
                  <span role="cell">{formatSentAt(b.sentAt || b.createdAt)}</span>
                  <span className="bc-history-reach" role="cell">
                    {formatHistoryReach(b)}
                  </span>
                  <span role="cell">
                    <span className={`bc-status bc-status--${b.status === "sent"}`}>
                      {b.status === "failed" ? "Failed" : "Delivered"}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </CreatorStudioLayout>
  );
}
